import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  Badge,
  Checkbox,
  Divider,
  Box,
  Spinner,
  Modal,
} from '@shopify/polaris';
import { EditIcon, SendIcon } from '@shopify/polaris-icons';
import { api } from '../utils/api';

interface NotificationTemplate {
  type: string;
  subject: string;
  bodyHtml: string;
  enabled: boolean;
}

const TEMPLATE_TYPES = [
  { type: 'shipped', label: 'Expédié', description: 'Envoyé quand la commande est expédiée' },
  { type: 'in_transit', label: 'En transit', description: 'Envoyé quand le colis est en cours d\'acheminement' },
  { type: 'out_for_delivery', label: 'En livraison', description: 'Envoyé quand le colis est en cours de livraison' },
  { type: 'delivered', label: 'Livré', description: 'Envoyé quand le colis est livré' },
  { type: 'exception', label: 'Exception', description: 'Envoyé en cas de problème de livraison' },
];

export default function Notifications() {
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testTemplateType, setTestTemplateType] = useState('');

  const { data: templates, isLoading } = useQuery<NotificationTemplate[]>({
    queryKey: ['notification-templates'],
    queryFn: () => api.get('/notifications/templates'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ type, enabled }: { type: string; enabled: boolean }) =>
      api.post(`/notifications/toggle/${type}`, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (template: NotificationTemplate) =>
      api.put(`/notifications/templates/${template.type}`, template),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
      setEditingTemplate(null);
    },
  });

  const testMutation = useMutation({
    mutationFn: ({ type, email }: { type: string; email: string }) =>
      api.post('/notifications/test', { type, email }),
    onSuccess: () => {
      setTestModalOpen(false);
      setTestEmail('');
    },
  });

  const handleToggle = useCallback((type: string, enabled: boolean) => {
    toggleMutation.mutate({ type, enabled });
  }, [toggleMutation]);

  const handleOpenTest = useCallback((type: string) => {
    setTestTemplateType(type);
    setTestModalOpen(true);
  }, []);

  const handleSendTest = useCallback(() => {
    if (testEmail && testTemplateType) {
      testMutation.mutate({ type: testTemplateType, email: testEmail });
    }
  }, [testEmail, testTemplateType, testMutation]);

  if (isLoading) {
    return (
      <Page title="Notifications">
        <Box padding="800">
          <InlineStack align="center">
            <Spinner size="large" />
          </InlineStack>
        </Box>
      </Page>
    );
  }

  return (
    <Page title="Notifications">
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Emails de notification
              </Text>
              <Text as="p" tone="subdued">
                Configurez les emails envoyés automatiquement à vos clients lors des mises à jour de suivi.
              </Text>
              <Divider />

              {TEMPLATE_TYPES.map((templateType) => {
                const template = templates?.find((t) => t.type === templateType.type);
                const isEnabled = template?.enabled ?? true;

                return (
                  <Box key={templateType.type} paddingBlockEnd="400">
                    <InlineStack align="space-between" blockAlign="center">
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="span" variant="bodyMd" fontWeight="semibold">
                            {templateType.label}
                          </Text>
                          <Badge tone={isEnabled ? 'success' : undefined}>
                            {isEnabled ? 'Activé' : 'Désactivé'}
                          </Badge>
                        </InlineStack>
                        <Text as="span" tone="subdued" variant="bodySm">
                          {templateType.description}
                        </Text>
                      </BlockStack>
                      <InlineStack gap="200">
                        <Button
                          size="slim"
                          onClick={() => handleOpenTest(templateType.type)}
                          icon={SendIcon}
                        >
                          Test
                        </Button>
                        <Button
                          size="slim"
                          onClick={() =>
                            setEditingTemplate(
                              template || {
                                type: templateType.type,
                                subject: '',
                                bodyHtml: '',
                                enabled: true,
                              }
                            )
                          }
                          icon={EditIcon}
                        >
                          Modifier
                        </Button>
                        <Checkbox
                          label=""
                          checked={isEnabled}
                          onChange={(checked) => handleToggle(templateType.type, checked)}
                        />
                      </InlineStack>
                    </InlineStack>
                    <Divider />
                  </Box>
                );
              })}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Variables disponibles
              </Text>
              <Divider />
              <Text as="p" variant="bodySm">
                Utilisez ces variables dans vos templates :
              </Text>
              <BlockStack gap="100">
                <code>{'{{order_name}}'}</code>
                <code>{'{{tracking_number}}'}</code>
                <code>{'{{carrier_name}}'}</code>
                <code>{'{{tracking_url}}'}</code>
                <code>{'{{status_detail}}'}</code>
                <code>{'{{delivered_at}}'}</code>
                <code>{'{{last_update}}'}</code>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Edit Template Modal */}
      {editingTemplate && (
        <Modal
          open={!!editingTemplate}
          onClose={() => setEditingTemplate(null)}
          title={`Modifier le template "${TEMPLATE_TYPES.find((t) => t.type === editingTemplate.type)?.label}"`}
          primaryAction={{
            content: 'Enregistrer',
            loading: updateMutation.isPending,
            onAction: () => updateMutation.mutate(editingTemplate),
          }}
          secondaryActions={[
            {
              content: 'Annuler',
              onAction: () => setEditingTemplate(null),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              <TextField
                label="Sujet"
                value={editingTemplate.subject}
                onChange={(value) =>
                  setEditingTemplate({ ...editingTemplate, subject: value })
                }
                autoComplete="off"
              />
              <TextField
                label="Contenu HTML"
                value={editingTemplate.bodyHtml}
                onChange={(value) =>
                  setEditingTemplate({ ...editingTemplate, bodyHtml: value })
                }
                multiline={10}
                autoComplete="off"
              />
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}

      {/* Test Email Modal */}
      <Modal
        open={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        title="Envoyer un email test"
        primaryAction={{
          content: 'Envoyer',
          loading: testMutation.isPending,
          onAction: handleSendTest,
          disabled: !testEmail,
        }}
        secondaryActions={[
          {
            content: 'Annuler',
            onAction: () => setTestModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <TextField
            label="Adresse email"
            type="email"
            value={testEmail}
            onChange={setTestEmail}
            autoComplete="email"
            placeholder="votre@email.com"
          />
        </Modal.Section>
      </Modal>
    </Page>
  );
}
